'use client';
import axios from '@/lib/axios';
import { ApiRoutes, Routes } from '@/settings/routes';
import React, { Fragment, useEffect, useState } from 'react';
import LinkFooter from '../layout/links/LinkFooter';
import TitleLinksFooter from '../layout/titles/TitleLinksFooter';
import Link from 'next/link';
import { App_text } from '@/settings/AppText';

function useFooterData() {
    const [data, setData] = useState({
        categories: { tours: [], packages: [], events: [], luxury_travels: [] },
    });

    useEffect(() => {
        axios
            .get(ApiRoutes.API_links_footer)
            .then((res) => {
                if (res.data.status === 200) setData(res.data);
            })
            .catch(console.error);
    }, []);

    return data.categories;
}

function FooterCategoryColumn({ title, href, categories = [], getHref, titleClickable = true }) {
    if (!categories.length) return null;

    return (
        <div className="flex flex-col gap-4">
            <TitleLinksFooter>
                {titleClickable && href ? (
                    <Link prefetch={false} className="transition-colors hover:text-main-color" href={href}>
                        {title}
                    </Link>
                ) : (
                    <span>{title}</span>
                )}
            </TitleLinksFooter>
            <ul className="flex flex-col gap-2">
                {categories.map((cat) => (
                    <li key={cat.id}>
                        <LinkFooter href={getHref(cat)}>{cat.title || cat.name}</LinkFooter>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function DynamicTravelGuidesFooter() {
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        axios
            .get(ApiRoutes.API_get_travel_guide_categories_front)
            .then((res) => {
                if (res.data.status === 200) {
                    const filtered = (res.data.categories || []).filter((cat) => cat.travel_guides_count > 0);
                    setCategories(filtered);
                }
            })
            .catch(console.error);
    }, []);

    if (!categories.length) return null;

    return <FooterCategoryColumn title={App_text.generale.navbar.travel_guide} href={Routes.FRONT_travel_guide_categories} categories={categories} getHref={(cat) => `${Routes.FRONT_travel_guide_category}${cat.slug}`} />;
}

export function DynamicToursFooter({ start = 0, end, title, titleClickable = true } = {}) {
    const { tours } = useFooterData();
    const visibleTours = tours?.slice(start, end) || [];

    if (!visibleTours.length) return null;

    return (
        <Fragment>
            {visibleTours.map((parent) => (
                <div key={parent.id} className="flex flex-col gap-4">
                    <TitleLinksFooter>
                        {titleClickable ? (
                            <Link prefetch={false} className="transition-colors hover:text-main-color" href={`${Routes.FRONT_tours}/category/${parent.slug}`}>
                                {title || parent.title}
                            </Link>
                        ) : (
                            <span>{title || parent.title}</span>
                        )}
                    </TitleLinksFooter>
                    {parent.children?.length > 0 && (
                        <ul className="flex flex-col gap-2">
                            {parent.children.map((child) => (
                                <li key={child.id}>
                                    <LinkFooter href={`${Routes.FRONT_tours}/category/${child.slug}`}>{child.title}</LinkFooter>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ))}
        </Fragment>
    );
}

export function DynamicPackagesFooter() {
    const { packages } = useFooterData();
    return <FooterCategoryColumn title={App_text.generale.navbar.packages} href={Routes.FRONT_packages} categories={packages || []} getHref={(cat) => `${Routes.FRONT_packages}/category/${cat.slug}`} />;
}

export function DynamicEventsFooter() {
    const { events } = useFooterData();
    return <FooterCategoryColumn title={App_text.generale.navbar.events} href={Routes.FRONT_events} categories={events || []} getHref={(cat) => `${Routes.FRONT_events}/category/${cat.slug}`} />;
}

export function DynamicLuxuryFooter() {
    const { luxury_travels } = useFooterData();
    return (
        <FooterCategoryColumn
            title={App_text.generale.navbar.luxury_travels}
            href={Routes.FRONT_luxury_travels}
            categories={luxury_travels || []}
            getHref={(cat) => `${Routes.FRONT_luxury_travels}/category/${cat.slug}`}
        />
    );
}

export function DynamicOthersFooter() {
    return (
        <Fragment>
            <DynamicPackagesFooter />
            <DynamicEventsFooter />
            <DynamicLuxuryFooter />
        </Fragment>
    );
}

export default function DynamicLinksFooter() {
    return null;
}
